import * as yup from "yup";

/* -------- CATEGORY GRID VALIDATIONS -------- */

export const categoryGridItemSchema = yup.object({
  title: yup
    .string()
    .required("Title is required")
    .min(2, "Title must be at least 2 characters"),

  icon: yup
    .string()
    .required("Icon is required"),

  landingUrl: yup
    .string()
    .required("Landing URL is required")
    .url("Enter a valid URL"),
});

export type CategoryGridItemFormValues = yup.InferType<
  typeof categoryGridItemSchema
>;

export const categoryGridSchema = yup.object({
  title: yup.string().min(1, "Item title is required"),
  icon: yup.string().min(1, "Item icon is required"),
  gridType: yup
    .string()
    .url("Type is required"),
});

export const categoryGridFormSchema = yup.object({
  widgetName: yup.string().required('Widget name is required'),
  widgetTitle: yup.string().required('Widget title is required'),
  widgetIcon: yup.string().required('Widget icon is required'),
  enabled: yup.boolean(),
  contentType: yup.string().required('Content type is required'),
  sourceType: yup.string().required('Source type is required'),
  sortingMethod: yup.string().when('sourceType', {
    is: 'All',
    then: (schema) => schema.required('Sorting method is required when source type is All'),
    otherwise: (schema) => schema,
  }),
  ctaLabel: yup.string().required('CTA label is required'),
});

export type CategoryGridFormValues = yup.InferType<typeof categoryGridFormSchema>;


/* -------- COLLECTION CARD VALIDATIONS -------- */

const COLLECTION_CARD_MAX_ITEMS = 4;

export const validateCollectionCardIconFile = (file: File | null): boolean => {
  if (!file) return false;
  if (file.size > 5 * 1024 * 1024) return false;
  const allowedTypes = ['image/svg+xml', 'image/svg'];
  const fileName = file.name.toLowerCase();
  return allowedTypes.includes(file.type) || fileName.endsWith('.svg');
};

export const collectionCardValidationSchema = yup.object({
  sourceType: yup
    .string()
    .oneOf(['category', 'manual'], 'Please select a valid source type')
    .required('Source type is required'),
  category: yup
    .object()
    .nullable()
    .when('sourceType', {
      is: 'category',
      then: (schema) =>
        schema
          .required('Category is required')
          .test('has-id', 'Please select a valid category', (value) => {
            return !!(value && value.id);
          }),
      otherwise: (schema) => schema.nullable().notRequired(),
    }),
  sortingMethod: yup
    .string()
    .when('sourceType', {
      is: 'category',
      then: (schema) =>
        schema
          .required('Sorting method is required')
          .oneOf(['DEFAULT', 'HIGHEST ENGAGEMENT', 'NEWEST'], 'Invalid sorting method'),
      otherwise: (schema) => schema.notRequired(),
    }),
  widgetTitle: yup
    .string()
    .when('sourceType', {
      is: 'manual',
      then: (schema) =>
        schema.required('Widget title is required').min(1, 'Widget title cannot be empty'),
      otherwise: (schema) => schema.notRequired(),
    }),
  widgetIcon: yup
    .string()
    .when('sourceType', {
      is: 'manual',
      then: (schema) =>
        schema.required('Widget icon is required'),
      otherwise: (schema) =>
        schema.notRequired(),
    }),
  widgetIconPreview: yup.mixed().nullable().notRequired(),
  heroItems: yup
    .array()
    .when('sourceType', {
      is: 'manual',
      then: (schema) =>
        schema
          .min(1, 'Please add at least one item')
          .max(COLLECTION_CARD_MAX_ITEMS, `Maximum ${COLLECTION_CARD_MAX_ITEMS} items allowed`)
          .required('At least one item is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  collectionItems: yup
    .array()
    .when('sourceType', {
      is: 'category',
      then: (schema) =>
        schema
          .min(1, 'Please add at least one item')
          .max(COLLECTION_CARD_MAX_ITEMS, `Maximum ${COLLECTION_CARD_MAX_ITEMS} items allowed`)
          .required('At least one item is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export type CollectionCardFormValues = yup.InferType<typeof collectionCardValidationSchema>;

export { COLLECTION_CARD_MAX_ITEMS };

/* -------- WATCH HISTORY VALIDATIONS -------- */

const WATCH_HISTORY_MAX_ITEMS = 10;

export const watchHistoryFormSchema = yup.object({
  widgetTitle: yup.string().trim().required('Widget title is required'),
  maxItems: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value
    )
    .typeError('Max items is required')
    .required('Max items is required')
    .min(1, `Max items must be between 1 and ${WATCH_HISTORY_MAX_ITEMS}`)
    .max(WATCH_HISTORY_MAX_ITEMS, `Max items must be between 1 and ${WATCH_HISTORY_MAX_ITEMS}`),
  widgetIcon: yup.string().required('Widget icon is required'),
});

export type WatchHistoryFormValues = yup.InferType<typeof watchHistoryFormSchema>;

export { WATCH_HISTORY_MAX_ITEMS };
